import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup } from '@angular/forms';

import { Form } from 'app/shared/form';
import { HttpErrorHandler } from 'app/shared/http-error-handler';

import { Notification, NotificationSender } from 'app/_models/notification';
import { NotificationService } from 'app/_services/notification.service';
import { NotificationSenderService } from 'app/_services/notification-sender.service';
import { ShipownerService } from 'app/_services/shipowner.service';
import { ShipService } from 'app/_services/ship.service';
import { Shipowner } from 'app/_models/shipowner';
import { ShipTypeAssociation } from 'app/_models/ship-type';

@Component({
  selector: 'app-ship-form',
  templateUrl: './notification-form.component.html',
})
export class NotificationFormComponent extends Form implements OnInit {
  form: FormGroup;
  notification: Notification;
  senders: NotificationSender[];
  recipients: Shipowner[] = [];
  recipientsIDs: string[] = [];
  shipTypes: ShipTypeAssociation[] = [];
  selectedRecipients: string[] = [];
  allChecked = false;
  lockCheckbox = false;
  fileError = '';

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService,
    private notificationSenderService: NotificationSenderService,
    private shipownerService: ShipownerService,
    private shipService: ShipService,
    protected route: ActivatedRoute,
    protected router: Router,
    private location: Location,
    protected httpErrorHandler: HttpErrorHandler,
    private cdref: ChangeDetectorRef
  ) {
    super(route, httpErrorHandler);
    this.form = fb.group({
      subject: '',
      body: '',
      sender: '',
    });
  }

  ngOnInit() {
    this.getSenders();
    this.getNotification();
    this.getShipTypes();
    this.setFormMode('update');
  }

  isReadonly() {
    return this.notification.status === 'send';
  }

  getShipTypes() {
    this.shipService.getAssociationTypes().subscribe(
      (data) => {
        this.shipTypes = ShipTypeAssociation.generateCollectionFromEndpoint(data);
      },
      (error) => this.handleError(error)
    );
  }

  getSenders() {
    this.notificationSenderService.rawList().subscribe(
      (data) => {
        this.senders = NotificationSender.generateCollectionFromApi(data);
      },
      (error) => this.handleError(error)
    );
  }

  getRecipients() {
    this.shipownerService.rawList().subscribe(
      (data) => {
        data.forEach((item) => {
          this.recipients.push(new Shipowner().apiMap(item));
        });
        this.recipientsIDs = [];
        this.recipients.forEach((item) => {
          this.recipientsIDs.push(`${item.id}`);
        });
        this.isRecipientesChecked();

        this.cdref.detectChanges();
      },
      (error) => this.handleError(error)
    );
  }

  isRecipientesChecked() {
    const d = this.recipientsIDs.filter((x) => this.selectedRecipients.includes(x));
    this.allChecked = d.length === this.recipientsIDs.length;
  }

  checkAll() {
    const method = this.allChecked ? 'add' : 'remove';
    this.lockCheckbox = true;
    this.notificationService.attachAll(this.notification, method).subscribe(
      (response) => {
        this.notification.mapRecipients(response);
        this.selectedRecipients = this.notification.recipientsIDs;
        this.isRecipientesChecked();
        this.lockCheckbox = false;
      },
      (error) => {
        this.handleError(error);
        this.lockCheckbox = false;
      }
    );
  }

  getNotification() {
    this.route.params.forEach((params: Params) => {
      const id = +params['id'];
      this.notificationService.getNotification(id).subscribe(
        (notification) => {
          this.notification = new Notification().apiMap(notification);
          this.selectedRecipients = Array.from(this.notification.recipientsIDs);
          this.getRecipients();
        },
        (error) => this.handleError(error)
      );
    });
  }

  handleError(error) {
    const errors = this.httpErrorHandler.handleHttpError(error);
    this.parseApiErrors(errors, this.form);
  }

  updateNotification(send = false) {
    this.notificationService.update(this.notification).subscribe(
      (notification) => this.onUpdate(notification, send),
      (error) => this.handleError(error)
    );
  }

  onUpdate(notification, send = false) {
    if (send) {
      this.send();
    }
  }

  goBack(): void {
    this.location.back();
  }
  uploadAttachmentFiles(files: File[]) {
    let error_fichero = false;
    for (const file of Array.from(files)) {
      if (file.size < 4 * 1024 * 1024) {
        this.notificationService.attachmentFile(this.notification, file).subscribe(
          (snts) => {
            this.notificationService.get(this.notification.id).subscribe(
              (response) => {
                this.notification.attached_files = response.attached_files;
              },
              (error) => {
                this.handleError(error);
              }
            );
          },
          (error) => {
            this.handleError(error);
            this.fileError = error.body.file && error.body.file[0];
          }
        );
      } else {
        error_fichero = true;
      }
    }
    if (error_fichero) {
      this.fileError = 'Archivo demasiado pesado, no debe superar los 4 MiB';
    } else {
      this.fileError = '';
    }
  }
  removeAttachmentFile(file_id) {
    this.notificationService.removeAttachmentFile(this.notification, file_id).subscribe(
      (response) => {
        this.notification.apiMap(response);
        this.notification.attached_files = response.attached_files;
        this.fileError = '';
      },
      (error) => {
        this.fileError = error.body;
        this.handleError(error);
      }
    );
  }

  attachRecipient(shipowner) {
    const method = this.selectedRecipients.includes(`${shipowner.id}`) ? 'add' : 'remove';
    this.notificationService.attachShipowner(this.notification, shipowner, method).subscribe(
      (response) => {
        this.notification.mapRecipients(response);
        this.isRecipientesChecked();
      },
      (error) => this.handleError(error)
    );
  }

  recipientsByType(value) {
    if (!value || value === '') {
      return;
    }

    this.lockCheckbox = true;
    this.notificationService.attachByShiptype(this.notification, value).subscribe(
      (response) => {
        this.notification.mapRecipients(response);
        this.selectedRecipients = this.notification.recipientsIDs;
        this.isRecipientesChecked();
        this.lockCheckbox = false;
      },
      (error) => {
        this.handleError(error);
        this.lockCheckbox = false;
      }
    );
  }

  sendNotification() {
    this.updateNotification(true);
  }

  send() {
    this.notificationService.send(this.notification).subscribe(
      (response) => {
        this.goBack();
      },
      (error) => this.handleError(error)
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/notifications/notification-form/notification-form.component.ts